import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { MapView } from '../components/MapView'
import { SaveLocationButton } from '../components/SaveLocationButton'
import {
  apiFetch,
  formatApiError,
  useLocation,
  useReviews,
} from '../shared/api/client'
import { detailReportSchema, detailReviewSchema } from '../shared/api/schemas'
import type { Review } from '../shared/api/types'
import { formatAverage, formatRegion } from '../shared/lib/location-formatters'
import { trackEvent } from '../shared/lib/analytics'
import { Seo } from '../shared/lib/seo'
import { scoreOptions } from '../shared/lib/score-options'

type ReviewFormInput = z.input<typeof detailReviewSchema>
type ReviewFormValues = z.output<typeof detailReviewSchema>

type ReportFormInput = z.input<typeof detailReportSchema>
type ReportFormValues = z.output<typeof detailReportSchema>

function DetailScoreCard({
  label,
  value,
}: {
  label: string
  value: number | string | null | undefined
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 md:p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-slate-900 md:text-xl">{value ?? '–'}</p>
    </div>
  )
}

function ReviewList({
  reviews,
  onReportReview,
}: {
  reviews: Review[]
  onReportReview: (reviewId: string) => void
}) {
  if (!reviews.length) {
    return (
      <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
        Še ni objavljenih mnenj za to lokacijo.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {reviews.map((review) => (
        <article
          key={review.id}
          className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                {review.recommendation_score}/5 priporočilo
              </span>
              <span className="text-xs text-slate-500">
                Zasebnost {review.privacy_score} · Dostop {review.access_score}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onReportReview(review.id)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            >
              Prijavi mnenje
            </button>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-700">
            {review.comment || 'Uporabnik ni pustil dodatnega komentarja.'}
          </p>
        </article>
      ))}
    </div>
  )
}

function ReviewScoreField({
  label,
  idPrefix,
  registration,
}: {
  label: string
  idPrefix: string
  registration: UseFormRegisterReturn
}) {
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex gap-2">
        {scoreOptions().map((score) => (
          <label key={`${idPrefix}-${score}`} className="flex-1 cursor-pointer">
            <input
              type="radio"
              value={score}
              {...registration}
              className="peer sr-only"
            />
            <div className="rounded-xl border border-slate-200 bg-slate-50 py-2.5 text-center text-sm font-bold text-slate-600 transition peer-checked:border-emerald-500 peer-checked:bg-emerald-50 peer-checked:text-emerald-700 active:scale-95">
              {score}
            </div>
          </label>
        ))}
      </div>
    </div>
  )
}

export function LocationDetail() {
  const { slug } = useParams()
  const queryClient = useQueryClient()
  const location = useLocation(slug)
  const reviews = useReviews(location.data?.id)
  const reviewResults = reviews.data?.results ?? location.data?.reviews ?? []
  const [detailReportTargetType, setDetailReportTargetType] = useState<'location' | 'review'>(
    'location',
  )
  const [reportSuccessMessage, setReportSuccessMessage] = useState('')

  const reviewForm = useForm<ReviewFormInput, unknown, ReviewFormValues>({
    resolver: zodResolver(detailReviewSchema),
    defaultValues: {
      recommendation_score: 5,
      privacy_score: 4,
      access_score: 4,
      comment: '',
      visit_date: '',
    },
  })

  const reportForm = useForm<ReportFormInput, unknown, ReportFormValues>({
    resolver: zodResolver(detailReportSchema),
    defaultValues: {
      target_type: 'location',
      target_id: '',
      reason: 'spam',
      details: '',
    },
  })

  const reviewMutation = useMutation({
    mutationFn: (values: ReviewFormValues) =>
      apiFetch('/reviews', {
        method: 'POST',
        body: JSON.stringify({
          location: location.data?.id,
          ...values,
          visit_date: values.visit_date ?? null,
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reviews', location.data?.id] })
      await queryClient.invalidateQueries({ queryKey: ['location', slug] })
      reviewForm.reset({
        recommendation_score: 5,
        privacy_score: 4,
        access_score: 4,
        comment: '',
        visit_date: '',
      })
    },
  })

  const reportMutation = useMutation({
    mutationFn: (values: ReportFormValues) =>
      apiFetch('/reports', {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: async (_, values) => {
      setReportSuccessMessage(
        values.target_type === 'review' ? 'Mnenje je bilo prijavljeno.' : 'Lokacija je bila prijavljena.',
      )
      reportForm.reset({
        target_type: detailReportTargetType,
        target_id:
          detailReportTargetType === 'review'
            ? reviewResults[0]?.id ?? ''
            : location.data?.id ?? '',
        reason: 'spam',
        details: '',
      })
    },
  })

  useEffect(() => {
    if (!location.data) return

    reportForm.setValue('target_type', detailReportTargetType)
    reportForm.setValue(
      'target_id',
      detailReportTargetType === 'review' ? reviewResults[0]?.id ?? '' : location.data.id,
    )
  }, [detailReportTargetType, location.data, reportForm, reviewResults])

  useEffect(() => {
    if (!location.data) return
    trackEvent('location.view', {
      page: `/locations/${location.data.slug}`,
      target_id: location.data.id,
      label: location.data.title,
      region: location.data.region_code,
    })
  }, [location.data])

  const focusReportForm = () => {
    const reportSection = document.getElementById('report-content-section')
    if (reportSection && typeof reportSection.scrollIntoView === 'function') {
      reportSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const prepareLocationReport = () => {
    setReportSuccessMessage('')
    setDetailReportTargetType('location')
    reportForm.setValue('target_type', 'location')
    reportForm.setValue('target_id', location.data?.id ?? '')
    focusReportForm()
  }

  const prepareReviewReport = (reviewId: string) => {
    setReportSuccessMessage('')
    setDetailReportTargetType('review')
    reportForm.setValue('target_type', 'review')
    reportForm.setValue('target_id', reviewId)
    focusReportForm()
  }

  return (
    <main className="min-h-0 flex-1 overflow-y-auto">
      {location.data ? (
        <Seo
          title={location.data.title}
          description={location.data.short_description || location.data.description || 'Skupnostno preverjena piknik lokacija na Fukodromu.'}
          path={`/locations/${location.data.slug}`}
          structuredData={{
            '@context': 'https://schema.org',
            '@type': 'TouristAttraction',
            name: location.data.title,
            description:
              location.data.short_description || location.data.description || 'Piknik lokacija',
            url: `${(import.meta.env.VITE_SITE_URL || 'https://fukodrom.com').replace(/\/$/, '')}/locations/${location.data.slug}`,
            geo:
              location.data.latitude !== null && location.data.longitude !== null
                ? {
                    '@type': 'GeoCoordinates',
                    latitude: location.data.latitude,
                    longitude: location.data.longitude,
                  }
                : undefined,
          }}
        />
      ) : null}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-6">
        <div>
          <Link
            to="/"
            className="text-sm font-semibold text-emerald-700 transition hover:text-emerald-800"
          >
            ← Nazaj na zemljevid
          </Link>
        </div>

        {location.isLoading ? (
          <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-sm">
            <p className="text-sm text-slate-500">Nalagam lokacijo…</p>
          </div>
        ) : null}

        {location.isError ? (
          <div className="rounded-[32px] border border-rose-200 bg-rose-50 p-8 text-sm text-rose-700">
            {formatApiError(location.error)}
          </div>
        ) : null}

        {location.data ? (
          <>
            <section className="flex flex-col gap-6 lg:grid lg:grid-cols-[1.2fr_0.8fr]">
              <article className="order-2 rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm lg:order-1">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                      Location Detail
                    </p>
                    <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                      {location.data.title}
                    </h1>
                    <p className="mt-2 text-sm text-slate-500">
                      {formatRegion(location.data.region_code)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800">
                      {formatAverage(location.data.review_summary?.recommendation_average)}
                    </span>
                    <SaveLocationButton locationId={location.data.id} />
                    <button
                      type="button"
                      onClick={prepareLocationReport}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                    >
                      Prijavi lokacijo
                    </button>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-7 text-slate-600">
                  {location.data.description ||
                    location.data.short_description ||
                    'Opis lokacije še ni dodan.'}
                </p>

                <div className="mt-6 grid grid-cols-2 gap-3 xl:grid-cols-3">
                  <DetailScoreCard label="Trust" value={location.data.trust_score} />
                  <DetailScoreCard label="Visibility" value={location.data.visibility_score} />
                  <DetailScoreCard label="Reviews" value={location.data.review_summary?.count ?? '0'} />
                  <DetailScoreCard label="Privacy" value={location.data.attributes?.privacy_level ?? '–'} />
                  <DetailScoreCard label="Access" value={location.data.attributes?.access_quality ?? '–'} />
                  <DetailScoreCard label="Scenery" value={location.data.attributes?.scenery_level ?? '–'} />
                </div>

                {(location.data.attributes?.warning_terms ?? []).length ? (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {location.data.attributes?.warning_terms.map((term) => (
                      <span
                        key={term.id}
                        className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-amber-900"
                      >
                        {term.label}
                      </span>
                    ))}
                  </div>
                ) : null}
              </article>

              <div className="order-1 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm lg:order-2">
                <div className="h-[280px] w-full lg:h-full lg:min-h-[400px]">
                  <MapView locations={[location.data]} fitToLocations />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                    Reviews
                  </p>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                    Mnenja obiskovalcev
                  </h2>
                </div>
                <span className="text-sm text-slate-500">
                  {location.data.review_summary?.count ?? reviewResults.length} objav
                </span>
              </div>
              <ReviewList reviews={reviewResults} onReportReview={prepareReviewReport} />
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <article className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                  Add Review
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                  Dodaj mnenje
                </h2>

                <form
                  className="mt-6 grid gap-4 sm:grid-cols-2"
                  onSubmit={reviewForm.handleSubmit((values) => reviewMutation.mutate(values))}
                >
                  <ReviewScoreField
                    label="Recommendation"
                    idPrefix="recommendation"
                    registration={reviewForm.register('recommendation_score')}
                  />

                  <ReviewScoreField
                    label="Privacy"
                    idPrefix="privacy"
                    registration={reviewForm.register('privacy_score')}
                  />

                  <ReviewScoreField
                    label="Access"
                    idPrefix="access"
                    registration={reviewForm.register('access_score')}
                  />

                  <label className="text-sm font-medium text-slate-700">
                    Visit date
                    <input
                      {...reviewForm.register('visit_date')}
                      type="date"
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                    />
                  </label>

                  <label className="sm:col-span-2 text-sm font-medium text-slate-700">
                    Comment
                    <textarea
                      {...reviewForm.register('comment')}
                      rows={4}
                      placeholder="Was it quiet, accessible, worth recommending?"
                      className="mt-2 w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                    />
                  </label>

                  <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      disabled={reviewMutation.isPending}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {reviewMutation.isPending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : null}
                      <span>Submit review</span>
                    </button>
                    {reviewMutation.isSuccess ? (
                      <p className="text-sm text-emerald-700">Review submitted.</p>
                    ) : null}
                    {reviewMutation.isError ? (
                      <p className="text-sm text-rose-600">
                        {formatApiError(reviewMutation.error)}
                      </p>
                    ) : null}
                  </div>
                </form>
              </article>

              <article
                id="report-content-section"
                className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                  Moderation
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                  Report this content
                </h2>

                <form
                  className="mt-6 grid gap-4"
                  onSubmit={reportForm.handleSubmit((values) => reportMutation.mutate(values))}
                >
                  <label className="text-sm font-medium text-slate-700">
                    Target type
                    <select
                      {...reportForm.register('target_type')}
                      onChange={(event) => {
                        const value = event.target.value as 'location' | 'review'
                        setReportSuccessMessage('')
                        setDetailReportTargetType(value)
                        reportForm.setValue('target_type', value)
                        reportForm.setValue(
                          'target_id',
                          value === 'review' ? reviewResults[0]?.id ?? '' : location.data?.id ?? '',
                        )
                      }}
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                    >
                      <option value="location">Location</option>
                      <option value="review">Review</option>
                    </select>
                  </label>

                  <label className="text-sm font-medium text-slate-700">
                    {detailReportTargetType === 'review' ? 'Review to report' : 'Location to report'}
                    {detailReportTargetType === 'review' ? (
                      <select
                        {...reportForm.register('target_id')}
                        className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                      >
                        {reviewResults.map((review) => (
                          <option key={review.id} value={review.id}>
                            {review.comment
                              ? `${review.comment.slice(0, 48)}${review.comment.length > 48 ? '…' : ''}`
                              : `${review.recommendation_score}/5 review`}
                          </option>
                        ))}
                        {!reviewResults.length ? (
                          <option value="">No reviews available yet</option>
                        ) : null}
                      </select>
                    ) : (
                      <input
                        value={location.data.title}
                        readOnly
                        className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-500 outline-none"
                      />
                    )}
                  </label>

                  <label className="text-sm font-medium text-slate-700">
                    Reason
                    <input
                      {...reportForm.register('reason')}
                      placeholder="spam / private_property / unsafe"
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                    />
                  </label>

                  <label className="text-sm font-medium text-slate-700">
                    Details
                    <textarea
                      {...reportForm.register('details')}
                      rows={4}
                      placeholder="Helpful context for moderators"
                      className="mt-2 w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                    />
                  </label>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      disabled={reportMutation.isPending}
                      className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {reportMutation.isPending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : null}
                      <span>Submit report</span>
                    </button>
                    {reportSuccessMessage ? (
                      <p className="text-sm text-emerald-700">{reportSuccessMessage}</p>
                    ) : null}
                    {reportMutation.isError ? (
                      <p className="text-sm text-rose-600">
                        {formatApiError(reportMutation.error)}
                      </p>
                    ) : null}
                  </div>
                </form>
              </article>
            </section>
          </>
        ) : null}
      </div>
    </main>
  )
}
